import React, { useCallback, useMemo } from 'react';

import {
  IonIcon,
  IonRouterOutlet,
  IonTabBar,
  IonTabButton,
  IonTabs,
} from '@ionic/react';
import { IonReactRouter } from '@ionic/react-router';
import {
  documentTextOutline,
  informationCircleOutline,
  timeOutline,
} from 'ionicons/icons';

import { Redirect, Route } from 'react-router-dom';

import Actions, { Action } from 'components/Actions';
import ErrorContent from 'components/ErrorContent';
import Modal, {
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalTitle,
} from 'components/Modal';
import Refresher from 'components/Refresher';
import Typography from 'components/Typography';
import { useIsIncluded } from 'hooks/useIsIncluded';
import { useQueryClient } from 'query';
import {
  ACTION_APPROVE,
  ACTION_MORE_INFO,
  ACTION_REJECT,
  ACTION_UNDO,
  TAB_ACTIONS_LOG,
  TAB_DETAILS,
  TAB_INVOICES,
} from 'scenes/FinancePage/FR/constants';
import { useIsUpdating } from 'scenes/FinancePage/FR/data/useIsUpdating';
import { useLoadFR } from 'scenes/FinancePage/FR/data/useLoadFR';

import { useApprove, useMoreInfo, useReject, useUndo } from '../data/mutations';
import { details } from '../data/queryKeyFactory';
import ActionsLog from './components/ActionsLog';
import Details from './components/Details';
import DetailsSkeleton from './components/DetailsSkeleton';
import Invoices from './components/Invoices';

const FRDetailsModal = ({
  id,
  parentPath,
  onClose = () => {},
  onSubmit = () => {},
}: any) => {
  const { data, isRefetching, isLoading, isError, error, isSuccess } =
    useLoadFR({ id });
  const isUpdating = useIsUpdating();
  const { invoices, actionsLog, ...detailsData } = data || {};
  const queryClient = useQueryClient();
  const isActionAllowed = useIsIncluded(data?.actions);

  const [approve] = useApprove();
  const [reject] = useReject();
  const [undo] = useUndo();
  const [moreInfo] = useMoreInfo();

  const handleApprove = useCallback(() => {
    approve({ id });
  }, [id, approve]);

  const handleReject = useCallback(() => {
    reject({ id });
  }, [id, reject]);

  const handleUndo = useCallback(() => {
    undo({ id });
  }, [id, undo]);

  const handleMoreInfo = useCallback(() => {
    moreInfo({ id });
  }, [id, moreInfo]);

  const routes = useMemo(() => {
    return {
      [TAB_DETAILS]: `${parentPath}/${TAB_DETAILS}`,
      [TAB_INVOICES]: `${parentPath}/${TAB_INVOICES}`,
      [TAB_ACTIONS_LOG]: `${parentPath}/${TAB_ACTIONS_LOG}`,
    };
  }, [parentPath]);

  const renderDetailsComponent = useMemo(() => {
    switch (true) {
      case isError: //Loaded with error response
        return <ErrorContent text={error} />;
      case isSuccess: //Loaded with normal response
        return <Details data={detailsData} />;
      case isLoading:
        return <DetailsSkeleton />; //No loaded
      default:
        return null;
    }
  }, [isLoading, isSuccess, isError, detailsData, error]);

  const handleRefresh = () => {
    queryClient.invalidateQueries(details({ id }));
  };

  return (
    <Modal onClose={onClose} onSubmit={onSubmit}>
      <span></span>
      <IonReactRouter>
        <IonTabs>
          <IonTabBar slot={'top'}>
            <IonTabButton tab={TAB_DETAILS} href={routes[TAB_DETAILS]}>
              <Typography variant={'caption'}>Details</Typography>
              <IonIcon icon={informationCircleOutline} />
            </IonTabButton>
            <IonTabButton
              tab={TAB_INVOICES}
              href={routes[TAB_INVOICES]}
              disabled={!invoices?.length}
            >
              <Typography variant={'caption'}>
                Invoices ({invoices?.length || 0})
              </Typography>
              <IonIcon icon={documentTextOutline} />
            </IonTabButton>
            <IonTabButton tab={TAB_ACTIONS_LOG} href={routes[TAB_ACTIONS_LOG]}>
              <Typography variant={'caption'}>Actions Log</Typography>
              <IonIcon icon={timeOutline} />
            </IonTabButton>
          </IonTabBar>

          <IonRouterOutlet>
            <Redirect exact path={parentPath} to={routes[TAB_DETAILS]} />
            <Route exact path={routes[TAB_DETAILS]}>
              <ModalContent isRefetching={isRefetching} isUpdating={isUpdating}>
                <Refresher onRefresh={handleRefresh} />
                {renderDetailsComponent}
              </ModalContent>
            </Route>
            <Route exact path={routes[TAB_INVOICES]}>
              <ModalContent isRefetching={isRefetching} isUpdating={isUpdating}>
                <Refresher onRefresh={handleRefresh} />
                <Invoices
                  data={invoices}
                  total={detailsData.totalInvoicesAmount}
                />
              </ModalContent>
            </Route>
            <Route exact path={routes[TAB_ACTIONS_LOG]}>
              <ModalContent isRefetching={isRefetching} isUpdating={isUpdating}>
                <Refresher onRefresh={handleRefresh} />
                <ActionsLog data={actionsLog} />
              </ModalContent>
            </Route>
          </IonRouterOutlet>
        </IonTabs>
      </IonReactRouter>

      <ModalFooter>
        <ModalCloseButton />
        <ModalTitle>FR #{id}</ModalTitle>
        <Actions header={'More actions'} limit={1}>
          {isActionAllowed(ACTION_APPROVE) && (
            <Action
              onClick={handleApprove}
              color={'primary'}
              label={'Approve'}
            />
          )}
          {isActionAllowed(ACTION_MORE_INFO) && (
            <Action onClick={handleMoreInfo} label={'Request more info'} />
          )}
          {isActionAllowed(ACTION_UNDO) && (
            <Action onClick={handleUndo} label={'Undo'} />
          )}
          {isActionAllowed(ACTION_REJECT) && (
            <Action onClick={handleReject} label={'Reject'} />
          )}
        </Actions>
      </ModalFooter>
    </Modal>
  );
};

export default FRDetailsModal;
